// src/lib/api/financial/payments.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, validatePositiveNumber } from '../shared/validation'

/**
 * Get all payments for a company
 */
const _getPayments = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      invoice:invoices(
        invoice_number, 
        total_amount,
        client:clients(name, email)
      )
    `)
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new payment
 */
const _createPayment = async (paymentData) => {
  validateRequired(paymentData.invoice_id, 'Invoice ID')
  validateRequired(paymentData.amount, 'Amount')
  validatePositiveNumber(paymentData.amount, 'Amount')
  
  const companyId = paymentData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('payments')
    .insert([{
      ...paymentData,
      company_id: companyId,
      created_by: userId
    }])
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Update a payment
 */
const _updatePayment = async (paymentId, paymentData) => {
  validateRequired(paymentId, 'Payment ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  if (paymentData.amount) {
    validatePositiveNumber(paymentData.amount, 'Amount')
  }
  
  const { data, error } = await supabase
    .from('payments')
    .update({
      ...paymentData,
      updated_at: new Date().toISOString()
    })
    .eq('id', paymentId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Delete a payment
 */
const _deletePayment = async (paymentId) => {
  validateRequired(paymentId, 'Payment ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('payments')
    .delete()
    .eq('id', paymentId)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get payments by invoice ID
 */
const _getPaymentsByInvoice = async (invoiceId, companyId = null) => {
  validateRequired(invoiceId, 'Invoice ID')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .eq('invoice_id', invoiceId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Get payment by ID
 */
const _getPaymentById = async (paymentId) => {
  validateRequired(paymentId, 'Payment ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      invoice:invoices(invoice_number, total_amount, status),
      client:clients(name, email)
    `)
    .eq('id', paymentId)
    .eq('company_id', companyId)
    .single()
  
  if (error) throw error
  return data
}

/**
 * Get payment summary for a company
 */
const _getPaymentSummary = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse({
      total_payments: 0,
      total_amount: 0,
      pending_amount: 0,
      paid_amount: 0
    })
  }

  const { data, error } = await supabase
    .from('payments')
    .select('amount, status')
    .eq('company_id', resolvedCompanyId)
  
  if (error) throw error
  
  const summary = data.reduce((acc, payment) => {
    acc.total_payments += 1
    acc.total_amount += payment.amount || 0
    
    if (payment.status === 'completed') {
      acc.paid_amount += payment.amount || 0
    } else {
      acc.pending_amount += payment.amount || 0
    }
    
    return acc
  }, {
    total_payments: 0,
    total_amount: 0,
    pending_amount: 0,
    paid_amount: 0
  })
  
  return createSuccessResponse(summary)
}

// Export wrapped functions
export const getPayments = withErrorHandling(_getPayments, 'fetch payments', 'payments')
export const createPayment = withErrorHandling(_createPayment, 'create payment', 'payment')
export const updatePayment = withErrorHandling(_updatePayment, 'update payment', 'payment')
export const deletePayment = withErrorHandling(_deletePayment, 'delete payment', 'payment')
export const getPaymentsByInvoice = withErrorHandling(_getPaymentsByInvoice, 'get payments by invoice', 'payments')
export const getPaymentById = withErrorHandling(_getPaymentById, 'get payment by ID', 'payment')
export const getPaymentSummary = withErrorHandling(_getPaymentSummary, 'get payment summary', 'payments')